/**
 * Sentry SDK wrapper (ADR-035/036/037, tech-spec §12.3, S35).
 *
 * The SDK is loaded lazily through a dynamic `import('@sentry/react')`
 * so the SDK chunk stays out of the initial bundle for users who never
 * opt in (per-feature spec NFR1: initial-route delta ≤ 5 KB gzipped).
 *
 * One module-level flag tracks whether the SDK is loaded and active.
 * Callers go through this wrapper for every Sentry interaction — the
 * subscriber (a later commit) never imports `@sentry/react` directly,
 * so toggling the consent flag never accidentally pulls the SDK into
 * the entry chunk.
 */

import {
  beforeBreadcrumb as scrubBeforeBreadcrumb,
  beforeSend as scrubBeforeSend,
  type SentryBreadcrumbLike,
  type SentryEventLike,
} from './scrubbers'

type SentryReact = typeof import('@sentry/react')

let active: SentryReact | null = null

export interface ObservabilityConfig {
  dsn: string
  release?: string
  tracesSampleRate: number
}

export function readConfig(): ObservabilityConfig | null {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (typeof dsn !== 'string' || dsn.length === 0) return null
  const release =
    typeof import.meta.env.VITE_GIT_SHA === 'string'
      ? import.meta.env.VITE_GIT_SHA
      : undefined
  return { dsn, release, tracesSampleRate: 0.1 }
}

export async function initObservability(consent: boolean): Promise<boolean> {
  if (!consent) return false
  if (active !== null) return true
  const config = readConfig()
  if (!config) return false

  const mod = (await import('@sentry/react')) as SentryReact
  mod.init({
    dsn: config.dsn,
    tunnel: '/api/v1/error-reporting/envelope',
    sendDefaultPii: false,
    tracesSampleRate: config.tracesSampleRate,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    release: config.release,
    integrations: [mod.browserTracingIntegration()],
    beforeBreadcrumb: (breadcrumb) =>
      scrubBeforeBreadcrumb(
        breadcrumb as unknown as SentryBreadcrumbLike,
      ) as typeof breadcrumb | null,
    beforeSend: (event) =>
      scrubBeforeSend(event as unknown as SentryEventLike) as typeof event,
  })
  active = mod
  return true
}

export async function closeObservability(): Promise<void> {
  if (active === null) return
  const mod = active
  active = null
  await mod.close()
}

export function captureExceptionIfEnabled(error: unknown): void {
  if (active === null) return
  active.captureException(error)
}

export function isObservabilityActive(): boolean {
  return active !== null
}

export function __resetObservabilityForTests(): void {
  active = null
}
